import React, { Children, useState, useEffect } from 'react';
import classnames from 'classnames';

// Utils
import { isElementOfType, sanitizeFragments } from '../../utilities/components';

// Components
import WpLabelled from '../labelled/WpLabelled';
import WpRadioInput from './WpRadioInput';

//Styles
import './WpRadioGroup.scss';

export type DirectionType = 'horizontal' | 'vertical';
const DEFAULT_DIRECTION_TYPE = 'vertical';

export interface Props {
  /** ID for the input */
  id: string;
  /** default value to have checked on load */
  defaultValue?: string;
  // TODO: (josh) - removing helpText until we figure out design
  /** Additional hint text to display */
  // helpText?: React.ReactNode;
  /** Label for the input */
  label: string;
  /** Hide the label, but keep on screen for screen readers */
  labelHidden?: boolean;
  /** Disable the input */
  disabled?: boolean;
  /** Error to display beneath the label */
  error?: string;
  /** Disable editing of the input */
  readOnly?: boolean;
  /** Automatically focus the input */
  autoFocus?: boolean;
  /**  Content to display inside the Radio Group */
  children?: React.ReactNode;
  // name: string;
  required?: boolean;
  /** Callback when value is changed */
  onChange?(value: string): void;
  direction?: DirectionType;
  showRequiredIndicator?: boolean;
}

const WpRadioGroup: React.FC<Props> = ({
  id,
  labelHidden,
  // helpText,
  label,
  error,
  disabled,
  children,
  onChange,
  defaultValue,
  direction = DEFAULT_DIRECTION_TYPE,
  required = false,
  showRequiredIndicator = true,
}: Props) => {
  const [selectedValue, setSelectedValue] = useState(null);

  const handleChange = value => {
    setSelectedValue(value);
    if (onChange) onChange(value);
  };

  useEffect(() => {
    if (defaultValue) {
      handleChange(defaultValue);
    }
  }, []);

  const sanitizedChildren = children && sanitizeFragments(children);

  const baseClassName = 'WpRadioGroup';
  const classes = classnames(baseClassName, `${baseClassName}--${direction}`);
  return (
    <div className={classes}>
      <WpLabelled
        label={label}
        error={error}
        id={id}
        labelHidden={labelHidden}
        showRequiredIndicator={required && showRequiredIndicator}
      >
        <div className={`${baseClassName}__choices`}>
          {Children.map(sanitizedChildren, (child: React.ReactElement<any>) => {
            if (child && isElementOfType(child, [WpRadioInput])) {
              return (
                <div className={`${baseClassName}__choices__choice`}>
                  {React.cloneElement(child, {
                    name: id,
                    disabled,
                    onChange: handleChange,
                    checked: selectedValue === child.props.value,
                  })}
                </div>
              );
            }
          })}
        </div>
      </WpLabelled>
    </div>
  );
};

export default WpRadioGroup;
